import { Injectable } from '@angular/core';
import { RequestsService } from '../requests.service';
import { ConfigService, JSONRPC } from '../../../_constants/config/config.service';
import { catchError, map, Observable } from 'rxjs';
import { HttpsRequestBody, XAPI_METHODS, XAPI_METHODS_NAMES, XAPIS } from '../../../../models/Core.model';

export interface Organisation {
    org_id: number;
    name: string;
    cif: string;
    city_region_id: number;
    city_region_name: string;
    address: string;
    recom: string;
    caen_no: number;
    termene: boolean;
};

@Injectable({
    providedIn: 'root'
})
export class ApiOrganisationsService {
    constructor(
        private readonly requestsService: RequestsService,
        private readonly configService: ConfigService
    ) { }

    /**
     * List all organisations
     * @returns The organisations array
     */
    public list(): Observable<Organisation[]> {
        const _body: HttpsRequestBody = {
            id: parseInt((Math.random() * 100).toString(), 10),
            jsonrpc: JSONRPC,
            method: XAPI_METHODS[XAPIS.organisations][XAPI_METHODS_NAMES.list]
        };

        return this.requestsService.post(this.configService.getServerUrlXapi(), XAPIS.organisations, _body).pipe(
            map((resp: any) => {
                // First check status (0 = success, 1 = error)
                if (resp?.status === 0) {
                    // Then check for result property
                    if (resp?.result) {
                        return resp.result;
                    }
                }
                // If status is 1 (error) or result is missing, throw the response
                throw resp;
            }),
            catchError((error: any) => {
                throw error;
            })
        );
    }

    /**
     * Get a single organisation by ID
     * @param params The request parameters containing org_id
     * @returns The organisation data
     */
    public getOne(data: any): Observable<Organisation> {

        const _body: HttpsRequestBody = {
            id: parseInt((Math.random() * 100).toString(), 10),
            jsonrpc: JSONRPC,
            method: XAPI_METHODS[XAPIS.organisations][XAPI_METHODS_NAMES.getOne],
            params: {
                cif: Number(data.cif),
                source: data.source,
                fresh: data.fresh,
                org_id: data.org_id
            }
        };

        return this.requestsService.post(this.configService.getServerUrlXapi(), XAPIS.organisationsCache, _body).pipe(
            map((resp: any) => {
                if (resp.result) {
                    return resp.result;
                }
                throw resp;
            }),
            catchError((error: any) => {
                throw error;
            })
        );
    }

    /**
     * Create a new organisation
     * @param params The organisation data
     * @returns The created organisation ID
     */
    public create(params: any): Observable<any> {
        const _body: HttpsRequestBody = {
            id: parseInt((Math.random() * 100).toString(), 10),
            jsonrpc: JSONRPC,
            method: XAPI_METHODS[XAPIS.organisations][XAPI_METHODS_NAMES.createOrganisation],
            params: {}
        };

        // Explicitly construct params
        const requestParams: any = {};
        
        if (params.city_region_id !== undefined) {
            requestParams.city_region_id = params.city_region_id;
        }
        if (params.address !== undefined) {
            requestParams.address = params.address;
        }
        if (params.name !== undefined) {
            requestParams.name = params.name;
        }
        if (params.cif !== undefined) {
            requestParams.cif = params.cif;
        }
        if (params.recom !== undefined) {
            requestParams.recom = params.recom;
        }
        if (params.caen_no !== undefined) {
            requestParams.caen_no = params.caen_no;
        }
        
        _body.params = requestParams;

        return this.requestsService.post(this.configService.getServerUrlXapi(), XAPIS.organisations, _body).pipe(
            map((resp: any) => {
                // First check status (0 = success, 1 = error)
                if (resp?.status === 0) {
                    // Return result if available, otherwise return full response
                    return resp.result || resp;
                }
                // If status is 1 (error), throw the response
                throw resp;
            }),
            catchError((error: any) => {
                throw error;
            })
        );
    }

    /**
     * Update an existing organisation
     * @param params The organisation data including org_id
     * @returns The update result
     */
    public update(params: any): Observable<any> {
        const _body: HttpsRequestBody = {
            id: parseInt((Math.random() * 100).toString(), 10),
            jsonrpc: JSONRPC,
            method: XAPI_METHODS[XAPIS.organisations][XAPI_METHODS_NAMES.updateOrganisation],
            params: {}
        };

        // Explicitly construct params
        const requestParams: any = {
            org_id: params.org_id
        };
        
        if (params.cif !== undefined) {
            requestParams.cif = params.cif;
        }
        if (params.name !== undefined) {
            requestParams.name = params.name;
        }
        if (params.city_region_id !== undefined) {
            requestParams.city_region_id = params.city_region_id;
        }
        if (params.address !== undefined) {
            requestParams.address = params.address;
        }
        if (params.recom !== undefined) {
            requestParams.recom = params.recom;
        }
        if (params.caen_no !== undefined) {
            requestParams.caen_no = params.caen_no;
        }
        
        _body.params = requestParams;

        return this.requestsService.post(this.configService.getServerUrlXapi(), XAPIS.organisations, _body).pipe(
            map((resp: any) => {
                // First check status (0 = success, 1 = error)
                if (resp?.status === 0) {
                    // Return result if available, otherwise return full response
                    return resp.result || resp;
                }
                // If status is 1 (error), throw the response
                throw resp;
            }),
            catchError((error: any) => {
                throw error;
            })
        );
    }

    /**
     * Delete an organisation
     * @param params The request parameters containing org_id
     * @returns The deletion result
     */
    public delete(params: any): Observable<any> {
        const _body: HttpsRequestBody = {
            id: parseInt((Math.random() * 100).toString(), 10),
            jsonrpc: JSONRPC,
            method: XAPI_METHODS[XAPIS.organisations][XAPI_METHODS_NAMES.deleteOrganisation],
            params: {
                org_id: params.org_id
            }
        };

        return this.requestsService.post(this.configService.getServerUrlXapi(), XAPIS.organisations, _body).pipe(
            map((resp: any) => {
                // First check status (0 = success, 1 = error)
                if (resp?.status === 0) {
                    // Return result if available, otherwise return full response
                    return resp.result || resp;
                }
                // If status is 1 (error), throw the response
                throw resp;
            }),
            catchError((error: any) => {
                throw error;
            })
        );
    }
}
